<template>
  <div
    class="timeline-container"
    :class="[direction === 'horizontal' ? 'w-full' : 'flex flex-col', customClass]"
  >
    <!-- Horizontal Timeline -->
    <div v-if="direction === 'horizontal'" class="relative w-full">
      <!-- Progress Line -->
      <div class="absolute top-3 left-0 w-full h-0.5 flex">
        <div
          class="h-full transition-all duration-500 ease-in-out border-t-2 border-dashed border-primary"
          :style="{ width: `${progressPercentage}%` }"
        ></div>
        <div
          class="h-full flex-grow border-t-2 border-dashed border-primary"
          :class="{ 'border-primary': useCustomColors }"
        ></div>
      </div>

      <!-- Steps -->
      <div class="relative flex justify-between">
        <!-- Static Order Status Timeline -->
        <template v-if="isOrderStatus">
          <div
            v-for="(step, index) in orderStatusSteps"
            :key="index"
            class="flex flex-col items-center"
            :style="{ width: `${100 / orderStatusSteps.length}%` }"
          >
            <!-- Step Circle -->
            <div
              class="w-6 h-6 rounded-full flex items-center justify-center z-10 transition-all duration-300"
              :class="[
                step === 'In Production' && index + 1 === currentStep
                  ? 'bg-[#FBBF24] text-white'
                  : index < currentStep
                    ? 'bg-primary text-white'
                    : 'bg-white border-2 border-primary text-gray-300'
              ]"
            >
              <svg
                v-if="index < currentStep"
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <!-- Step Label -->
            <div
              class="mt-2 text-sm text-center"
              :class="[index < currentStep ? 'text-gray-800 font-medium' : 'text-gray-400']"
            >
              {{ step }}
            </div>
          </div>
        </template>

        <!-- Dynamic Timeline -->
        <template v-else>
          <slot></slot>
        </template>
      </div>
    </div>

    <!-- Vertical Timeline -->
    <div v-else class="relative">
      <!-- Progress Line -->
      <div
        class="absolute left-5 top-0 h-full w-0.5 flex flex-col"
        :class="{ 'left-1/2 transform -translate-x-1/2': alternating }"
      >
        <div
          class="w-full bg-primary transition-all duration-500 ease-in-out"
          :style="{ height: `${progressPercentage}%` }"
        ></div>
        <div
          class="w-full flex-grow border-l-2 border-dashed border-gray-300"
          :class="{ 'border-primary': useCustomColors }"
        ></div>
      </div>

      <!-- Steps -->
      <div class="relative">
        <!-- Static Order Status Timeline -->
        <template v-if="isOrderStatus">
          <div
            v-for="(step, index) in orderStatusSteps"
            :key="index"
            class="flex mb-8 relative"
            :class="[
              alternating ? (index % 2 === 0 ? 'justify-start' : 'justify-end') : 'justify-start',
            ]"
          >
            <!-- Step Circle -->
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-300 absolute"
              :class="[
                index < currentStep
                  ? 'bg-primary text-white'
                  : 'bg-white border-2 border-gray-300 text-gray-300',
                alternating ? 'left-1/2 transform -translate-x-1/2' : 'left-0',
              ]"
            >
              <svg
                v-if="index < currentStep"
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span v-else>{{ index + 1 }}</span>
            </div>

            <!-- Content Container -->
            <div
              class="bg-white p-4 rounded-lg shadow-sm border"
              :class="[
                alternating ? (index % 2 === 0 ? 'ml-16 mr-auto' : 'mr-16 ml-auto') : 'ml-16',
                index < currentStep ? 'border-primary' : 'border-gray-200',
              ]"
            >
              <!-- Step Label -->
              <div
                class="font-medium"
                :class="[index < currentStep ? 'text-gray-800' : 'text-gray-400']"
              >
                {{ step }}
              </div>
            </div>
          </div>
        </template>

        <!-- Dynamic Timeline -->
        <template v-else>
          <slot></slot>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CustimooOrderFlowStatuses } from '@/composables';
import { computed, onMounted, provide, ref, watch } from 'vue';

// Define the component props
interface Props {
  // Whether to use the predefined order status flow
  isOrderStatus?: boolean;
  // Current active step (1-based index)
  currentStep?: number;
  // Direction of the timeline: 'horizontal' or 'vertical'
  direction?: 'horizontal' | 'vertical';
  // Whether to alternate item positions in vertical mode
  alternating?: boolean;
  // Custom CSS class for the container
  customClass?: string;
  // Whether to use custom colors defined in items
  useCustomColors?: boolean;
  // For order status flow: the current status
  orderStatus?: string;
  // Total number of items (used for calculating progress)
  totalItems?: number;
}

const props = withDefaults(defineProps<Props>(), {
  isOrderStatus: false,
  currentStep: 1,
  direction: 'horizontal',
  alternating: false,
  customClass: '',
  useCustomColors: false,
  orderStatus: '',
  totalItems: 0,
});

// Define emits
const emit = defineEmits(['update:currentStep']);

// Define the order status steps
const orderStatusSteps = [
  'Order Created',
  'Artwork Approval',
  'Sample Design',
  'In Production',
  'Order Shipped',
  'Order Completed',
];

// Reactive state
const internalCurrentStep = ref(props.currentStep);

// Provide context to child components
provide('timelineContext', {
  direction: computed(() => props.direction),
  alternating: computed(() => props.alternating),
  currentStep: computed(() => internalCurrentStep.value),
});

// Watch for prop changes
watch(
  () => props.currentStep,
  (newValue) => {
    internalCurrentStep.value = newValue;
  }
);

// Watch for order status changes
watch(
  () => props.orderStatus,
  () => {
    if (props.isOrderStatus) {
      updateOrderStatusStep();
    }
  }
);

// Initialize on mount
onMounted(() => {
  if (props.isOrderStatus) {
    updateOrderStatusStep();
  }
});

// Map order status to timeline step
const updateOrderStatusStep = () => {
  const status = props.orderStatus.toLowerCase();

  if (
    [
      CustimooOrderFlowStatuses.FACTORYREVIEW,
      CustimooOrderFlowStatuses.ORDERAPPROVE,
      CustimooOrderFlowStatuses.ORDERCANCEL,
    ].includes(status)
  ) {
    internalCurrentStep.value = 1;
  } else if (status === CustimooOrderFlowStatuses.FACTORYAPPROVED) {
    internalCurrentStep.value = 2;
  } else if (
    [
      CustimooOrderFlowStatuses.CUSTOMERREVIEW,
      CustimooOrderFlowStatuses.CUSTOMERAPPROVED,
      CustimooOrderFlowStatuses.CUSTOMERREJECTED,
    ].includes(status)
  ) {
    internalCurrentStep.value = 3;
  } else if (
    status === CustimooOrderFlowStatuses.ORDERINPRODUCTION ||
    status === CustimooOrderFlowStatuses.PRODUCTIONFILESUPLOADED
  ) {
    internalCurrentStep.value = 4;
  } else if (status === CustimooOrderFlowStatuses.ORDERSHIPPED) {
    internalCurrentStep.value = 5;
  } else if (status === CustimooOrderFlowStatuses.ORDERCOMPLETED) {
    internalCurrentStep.value = 6;
  } else {
    internalCurrentStep.value = 1; // Default to first step
  }
};

// Computed properties
const currentStep = computed(() => {
  return Math.min(
    internalCurrentStep.value,
    props.isOrderStatus ? orderStatusSteps.length : props.totalItems
  );
});

const progressPercentage = computed(() => {
  const total = props.isOrderStatus ? orderStatusSteps.length : props.totalItems;
  if (total <= 1) return 0;
  const completedSteps = Math.max(0, Math.min(currentStep.value - 1, total - 1));

  return (completedSteps / (total - 1)) * 100;
});
</script>

<style lang="scss" scoped>
.timeline-container {
  @apply relative max-w-full;

  &.vertical {
    @apply flex flex-col;
  }
}
</style>
